import {
  PageSearchData,
  PanuiData,
  Pakihi,
} from "@plinknz/tah-website-elements";

export interface SearchResult {
  title: string;
  slug: string;
  match: { pre: string; match: string; post: string; matchCount: number };
}

interface SearchPagesProps {
  searchString: string;
  pages: PageSearchData[];
}

interface SearchPanuiProps {
  searchString: string;
  newsItems: PanuiData[];
}

interface SearchPakihiProps {
  searchString: string;
  pakihis: Pakihi[];
  slug: string;
}

const maxCharCount = 30;

function escapeRegExp(value: string) {
  return value.replace(/[\\^$.|?*+()[\]{}]/g, "\\$&");
}

function transformHTML(html: string) {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&ndash;/g, "-")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function transformTimedKupu(kupu: string) {
  return kupu
    .replace(/::/g, ":")
    .replace(/^[\d.]+:/, "")
    .split(/:[\d.]+:/)
    .join(" ");
}

function textMatch(
  searchString: string,
  attribute: string,
  regex: RegExp,
  regexAll: RegExp
) {
  if (!attribute) {
    return undefined;
  }

  const matches = attribute.match(regex);

  if (!matches?.length) {
    return undefined;
  }

  let pre = attribute.substring(0, matches.index);
  if (pre.length > maxCharCount) {
    // Concatenate front
    pre = pre.substring(pre.length - maxCharCount);
    pre = pre.substring(pre.search(" "));
    pre = `...${pre}`;
  }

  let post = attribute.substring(searchString.length + matches.index);
  if (post.length > maxCharCount) {
    // Concatenate end

    post = post.substring(0, maxCharCount).split("").reverse().join("");
    post = post.substring(post.search(" ")).split("").reverse().join("");
    post = `${post}...`;
  }

  return {
    pre,
    match: matches[0],
    post,
    matchCount: attribute.match(regexAll).length,
  };
}

export function getPakihiPage(pages: PageSearchData[]): string {
  for (const { slug, content } of pages) {
    if (
      content.find(
        ({ __typename }) => __typename === "ComponentContentBlocksRarangiPakihi"
      )
    ) {
      return slug;
    }
  }

  return undefined;
}

export function searchPages({
  searchString,
  pages,
}: SearchPagesProps): SearchResult[] {
  const sanitizedSearch = escapeRegExp(searchString);
  const regex = new RegExp(sanitizedSearch, "i");
  const regexAll = new RegExp(sanitizedSearch, "ig");

  function pageFilter(page: PageSearchData) {
    const { slug, title, subtitle } = page;

    let retval: SearchResult | undefined;

    for (const attribute of [title, subtitle]) {
      const match = textMatch(searchString, attribute, regex, regexAll);

      if (match) {
        if (retval) {
          retval.match.matchCount += match.matchCount;
        } else {
          retval = { title, slug, match };
        }
      }
    }

    for (const component of page.content) {
      const attributes = [];
      // eslint-disable-next-line no-underscore-dangle
      switch (component.__typename) {
        case "ComponentContentBlocksPanuiGrid":
        case "ComponentContentBlocksPageGrid":
          attributes.push(component.title);
          break;
        case "ComponentContentBlocksYouTubePlayer":
        case "ComponentContentBlocksLinks":
          attributes.push(component.description, component.title);
          break;
        case "ComponentContentBlocksCallToAction":
          attributes.push(component.description, component.callToActionTitle);
          break;
        case "ComponentContentBlocksImageGallery":
        case "ComponentContentBlocksDownloadFiles":
          attributes.push(component.description, component.heading);
          break;
        case "ComponentContentBlocksQuote":
          attributes.push(component.text);
          break;
        case "ComponentContentBlocksWaiata":
          attributes.push(
            transformTimedKupu(component.timed_kupu),
            component.title
          );
          break;
        case "ComponentContentBlocksPictureList":
          attributes.push(component.profileHeading);
          component.profiles.forEach(
            ({
              title: componentTitle,
              subtitle: componentSubtitle,
              description,
            }) =>
              attributes.push(componentTitle, componentSubtitle, description)
          );
          break;
        case "ComponentContentBlocksContent":
          attributes.push(transformHTML(component.content));
          break;
        default:
        // Do nothing
      }

      for (const attribute of attributes) {
        const match = textMatch(searchString, attribute, regex, regexAll);

        if (match) {
          if (retval) {
            retval.match.matchCount += match.matchCount;
          } else {
            retval = { title, slug, match };
          }
        }
      }
    }

    return retval;
  }

  const matchedPages = pages.map(pageFilter).filter((page) => page);

  return matchedPages;
}

export function searchPanui({
  searchString,
  newsItems,
}: SearchPanuiProps): SearchResult[] {
  const sanitizedSearch = escapeRegExp(searchString);
  const regex = new RegExp(sanitizedSearch, "i");
  const regexAll = new RegExp(sanitizedSearch, "ig");

  function panuiFilter({ author, content, headline, slug }: PanuiData) {
    const attributes = [author, transformHTML(content), headline];
    let retval: SearchResult | undefined;

    for (const attribute of attributes) {
      const match = textMatch(searchString, attribute, regex, regexAll);

      if (match) {
        if (retval) {
          retval.match.matchCount += match.matchCount;
        } else {
          retval = { title: headline, slug: `/panui/${slug}`, match };
        }
      }
    }

    return retval;
  }

  const matchedPages = newsItems
    .map(panuiFilter)
    .filter((panui: SearchResult) => panui);

  return matchedPages;
}

export function searchPakihi({
  searchString,
  pakihis,
  slug,
}: SearchPakihiProps): SearchResult[] {
  const sanitizedSearch = escapeRegExp(searchString);
  const regex = new RegExp(sanitizedSearch, "i");
  const regexAll = new RegExp(sanitizedSearch, "ig");

  function pakihiFilter({
    address,
    benefits,
    details,
    email,
    industry,
    name,
    phone,
    region,
  }: Pakihi) {
    const attributes = [
      address,
      benefits,
      details,
      email,
      industry,
      name,
      phone,
      region,
    ];
    let retval: SearchResult | undefined;

    for (const attribute of attributes) {
      const match = textMatch(searchString, attribute, regex, regexAll);

      if (match) {
        if (retval) {
          retval.match.matchCount += match.matchCount;
        } else {
          retval = { title: name, slug, match };
        }
      }
    }

    return retval;
  }

  const matchedPages = pakihis
    .map(pakihiFilter)
    .filter((pakihi: SearchResult) => pakihi);

  return matchedPages;
}
